import { inject, Injectable } from '@angular/core';
import { FormControl, FormGroup, NonNullableFormBuilder, Validators } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { LocationFormGroup, LocationFormMode, TimeZonesEnum } from '@ep/shared';
import { Location } from '@shared/models';

export interface LocationCoordinatesControls {
  Latitude: FormControl<number | null>;
  Longitude: FormControl<number | null>;
}

export type LocationCoordinatesFormGroup = FormGroup<LocationCoordinatesControls>;

// The shared ep-location-form only takes the fields it renders, so the coordinates set by the map picker
// live in a sibling group. Validity, enabling and resetting all go through this parent.
export interface LocationEditorControls {
  Details: LocationFormGroup;
  Coordinates: LocationCoordinatesFormGroup;
}

export type LocationEditorFormGroup = FormGroup<LocationEditorControls>;

@Injectable()
export class LocationFormService {
  private readonly fb = inject(NonNullableFormBuilder);

  private readonly mode$$ = new BehaviorSubject<LocationFormMode>('add');
  readonly mode$ = this.mode$$.asObservable();

  private readonly locationForm: LocationEditorFormGroup = this.fb.group({
    Details: this.fb.group({
      Name: this.fb.control<string | null>(null, { validators: [Validators.required] }),
      Address: this.fb.control<string | null>(null, { validators: [Validators.required] }),
      PhoneNumber: this.fb.control<string | null>(null, { validators: [Validators.required] }),
      TimeZoneType: this.fb.control<TimeZonesEnum | null>(TimeZonesEnum.PACIFICTIME, {
        validators: [Validators.required],
      }),
    }),
    Coordinates: this.fb.group({
      Latitude: this.fb.control<number | null>(null, { validators: [Validators.required] }),
      Longitude: this.fb.control<number | null>(null, { validators: [Validators.required] }),
    }),
  });

  getForm(): LocationEditorFormGroup {
    return this.locationForm;
  }

  getMode(): LocationFormMode {
    return this.mode$$.value;
  }

  initializeForAdd(): void {
    this.mode$$.next('add');
    this.resetTo(null);
    this.locationForm.enable();
  }

  initializeForView(location: Location): void {
    this.mode$$.next('view');
    this.resetTo(location);
    this.locationForm.disable();
  }

  initializeForEdit(location: Location): void {
    this.mode$$.next('edit');
    this.resetTo(location);
    this.locationForm.enable();
  }

  private resetTo(location: Location | null): void {
    this.locationForm.reset({
      Details: {
        Name: location?.Name ?? null,
        Address: location?.Address ?? null,
        PhoneNumber: location?.PhoneNumber ?? null,
        TimeZoneType: location?.TimeZoneType ?? TimeZonesEnum.PACIFICTIME,
      },
      Coordinates: {
        Latitude: location?.Latitude ?? null,
        Longitude: location?.Longitude ?? null,
      },
    });
  }
}
